import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { getCountry } from "@/i18n/countries";

/**
 * Slide-out admin editor for per-country hero copy.
 *
 * One tab per language the country supports (from `getCountry().languages`).
 * Each tab has 5 inputs: eyebrow / title_1 / title_em / title_2 / subtitle.
 * Placeholder text shows the current i18n default so admins understand
 * what will render if they leave a field blank.
 *
 * Cascade at render time (`useLocalizedHero`):
 *   DB override (this drawer's output) → i18n hero_variants.<COUNTRY> → generic hero.*
 *
 * Save writes PUT /admin/countries/{code}/hero-copy/{language} — an
 * upsert per (code, language). "Reset to defaults" fires a DELETE.
 */
const FIELDS = [
    { key: "eyebrow", label: "Eyebrow", multiline: false, hint: "Small caps above the headline (e.g. MEMBERS CLUB · EST 2019)" },
    { key: "title_1", label: "Headline · part 1", multiline: false, hint: "First half of the two-part headline" },
    { key: "title_em", label: "Headline · gold accent", multiline: false, hint: "The word rendered in gold italic between the two parts" },
    { key: "title_2", label: "Headline · part 2", multiline: false, hint: "Second half of the headline" },
    { key: "subtitle", label: "Subtitle", multiline: true, hint: "One or two sentences under the headline" },
];

export default function CountryCopyDrawer({ open, onOpenChange, country }) {
    const { t, i18n } = useTranslation();
    // Languages supported by this country per `i18n/countries.js`.
    const languages = useMemo(
        () => (country ? getCountry(country.code).languages : []),
        [country]
    );
    const [activeLang, setActiveLang] = useState(languages[0] || "en");
    // Loaded values from the API, one row per language.
    const [rowsByLang, setRowsByLang] = useState({});
    // Local form state — mirrors rowsByLang but tracks edits.
    const [formByLang, setFormByLang] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingLang, setSavingLang] = useState(null);

    // Reset drawer state whenever we switch which country we're editing.
    useEffect(() => {
        if (!open || !country) return;
        setActiveLang(languages[0] || "en");
        setLoading(true);
        api.get(`/admin/countries/${country.code}/hero-copy`)
            .then((r) => {
                const map = {};
                (r.data.items || []).forEach((row) => {
                    map[row.language] = row;
                });
                setRowsByLang(map);
                // Seed the form with the loaded rows; unknown languages
                // start blank so placeholder i18n defaults are visible.
                const initialForm = {};
                languages.forEach((lang) => {
                    const src = map[lang] || {};
                    initialForm[lang] = Object.fromEntries(
                        FIELDS.map((f) => [f.key, src[f.key] || ""])
                    );
                });
                setFormByLang(initialForm);
            })
            .catch(() => toast.error("Couldn't load hero copy"))
            .finally(() => setLoading(false));
    }, [open, country, languages]);

    // i18n placeholder for a specific (country, lang, field). Uses
    // i18next's `{lng: lang}` option so we read from a language bundle
    // without changing the active app language (which would trigger
    // setState-during-render in every subscribed component).
    const placeholderFor = (lang, fieldKey) => {
        const variantKey = `hero_variants.${country.code}.${fieldKey}`;
        const genericKey = `hero.${fieldKey}`;
        const hasVariant = i18n.exists(variantKey, { lng: lang });
        return t(hasVariant ? variantKey : genericKey, { lng: lang }) || "";
    };

    const updateField = (lang, fieldKey, value) => {
        setFormByLang((prev) => ({
            ...prev,
            [lang]: { ...(prev[lang] || {}), [fieldKey]: value },
        }));
    };

    const saveLang = async (lang) => {
        setSavingLang(lang);
        try {
            const payload = Object.fromEntries(
                FIELDS.map((f) => [f.key, formByLang[lang]?.[f.key] ?? ""])
            );
            const { data } = await api.put(
                `/admin/countries/${country.code}/hero-copy/${lang}`,
                payload
            );
            setRowsByLang((prev) => ({ ...prev, [lang]: data }));
            toast.success(`${country.code} · ${lang} hero copy saved`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingLang(null);
        }
    };

    const resetLang = async (lang) => {
        setSavingLang(lang);
        try {
            await api.delete(
                `/admin/countries/${country.code}/hero-copy/${lang}`
            );
            setRowsByLang((prev) => {
                const next = { ...prev };
                delete next[lang];
                return next;
            });
            setFormByLang((prev) => ({
                ...prev,
                [lang]: Object.fromEntries(FIELDS.map((f) => [f.key, ""])),
            }));
            toast.success(`${country.code} · ${lang} reverted to defaults`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingLang(null);
        }
    };

    if (!country) return null;
    const localMeta = getCountry(country.code);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid="country-copy-drawer"
                className="w-full sm:max-w-2xl bg-cream overflow-y-auto p-0"
            >
                <SheetHeader className="px-8 pt-8 pb-4 border-b border-navy-100 bg-white">
                    <div className="flex items-center gap-3 mb-1">
                        <span className="text-3xl leading-none">{localMeta.flag}</span>
                        <div>
                            <SheetTitle className="font-display text-2xl text-navy-700">
                                {country.name} hero copy
                            </SheetTitle>
                            <SheetDescription className="text-navy-500 text-sm">
                                Edit the localised headline shown on the landing page.
                                Leave a field blank to fall back to the default translation.
                            </SheetDescription>
                        </div>
                    </div>
                </SheetHeader>

                {loading ? (
                    <div className="flex items-center gap-2 text-navy-500 py-16 justify-center">
                        <Loader2 className="animate-spin" size={16} />
                        Loading copy…
                    </div>
                ) : (
                    <div className="p-8 space-y-6">
                        <Tabs
                            value={activeLang}
                            onValueChange={setActiveLang}
                            data-testid="country-copy-tabs"
                        >
                            <TabsList className="bg-white border border-navy-100">
                                {languages.map((lang) => {
                                    const hasOverride = !!rowsByLang[lang];
                                    return (
                                        <TabsTrigger
                                            key={lang}
                                            value={lang}
                                            data-testid={`country-copy-tab-${lang}`}
                                            className="data-[state=active]:bg-navy-700 data-[state=active]:text-white gap-2"
                                        >
                                            {lang}
                                            {hasOverride && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                                            )}
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>

                            {languages.map((lang) => {
                                const values = formByLang[lang] || {};
                                const isSaving = savingLang === lang;
                                const hasOverride = !!rowsByLang[lang];
                                return (
                                    <TabsContent
                                        key={lang}
                                        value={lang}
                                        className="mt-6 space-y-5"
                                        data-testid={`country-copy-panel-${lang}`}
                                    >
                                        <div className="rounded-xl border border-navy-100 bg-white p-4 flex items-start gap-3">
                                            <Sparkles
                                                size={16}
                                                className="text-gold-600 flex-shrink-0 mt-0.5"
                                            />
                                            <div className="text-xs text-navy-500 leading-relaxed">
                                                Placeholders show the current i18n default.
                                                Anything you type here overrides that default
                                                for visitors browsing in <strong>{lang}</strong>.
                                                {hasOverride && (
                                                    <div className="text-gold-600 mt-1">
                                                        A saved override already exists for this
                                                        language — the gold dot on the tab is your
                                                        cue.
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {FIELDS.map((f) => (
                                            <div key={f.key}>
                                                <Label
                                                    htmlFor={`copy-${lang}-${f.key}`}
                                                    className="text-navy-700 text-xs uppercase tracking-widest"
                                                >
                                                    {f.label}
                                                </Label>
                                                <p className="text-[11px] text-navy-400 mt-0.5 mb-2">
                                                    {f.hint}
                                                </p>
                                                {f.multiline ? (
                                                    <Textarea
                                                        id={`copy-${lang}-${f.key}`}
                                                        data-testid={`country-copy-${lang}-${f.key}`}
                                                        value={values[f.key] || ""}
                                                        onChange={(e) =>
                                                            updateField(lang, f.key, e.target.value)
                                                        }
                                                        placeholder={placeholderFor(lang, f.key)}
                                                        rows={3}
                                                        maxLength={240}
                                                        className="bg-white border-navy-100"
                                                    />
                                                ) : (
                                                    <Input
                                                        id={`copy-${lang}-${f.key}`}
                                                        data-testid={`country-copy-${lang}-${f.key}`}
                                                        value={values[f.key] || ""}
                                                        onChange={(e) =>
                                                            updateField(lang, f.key, e.target.value)
                                                        }
                                                        placeholder={placeholderFor(lang, f.key)}
                                                        maxLength={240}
                                                        className="bg-white border-navy-100"
                                                    />
                                                )}
                                            </div>
                                        ))}

                                        <div className="flex items-center justify-end gap-3 pt-2 border-t border-navy-100">
                                            {hasOverride && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    onClick={() => resetLang(lang)}
                                                    disabled={isSaving}
                                                    data-testid={`country-copy-reset-${lang}`}
                                                    className="text-navy-500 hover:text-red-600 rounded-full"
                                                >
                                                    <RotateCcw size={14} className="mr-2" />
                                                    Reset to defaults
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                onClick={() => saveLang(lang)}
                                                disabled={isSaving}
                                                data-testid={`country-copy-save-${lang}`}
                                                className="bg-navy-700 hover:bg-navy-800 text-white rounded-full px-6"
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <Loader2
                                                            size={14}
                                                            className="mr-2 animate-spin"
                                                        />
                                                        Saving…
                                                    </>
                                                ) : (
                                                    <>
                                                        <Save size={14} className="mr-2" />
                                                        Save {lang}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </TabsContent>
                                );
                            })}
                        </Tabs>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
